import type { OrderType } from 'apiConnector/blotters';
import { connect } from 'react-redux';
import { blotterPanelItemReset } from 'state/blotters/blotterPanel/blotterPanel.action';
import { getSecondaryOrderBlotterPanelSelected } from 'state/blotters/blotterPanel/blotterPanel.selector';
import { getOrderById } from 'state/blotters/secondaryMarketOrderBlotter/secondaryMarketOrderBlotter.selector';
import { cpOrderCancellationSelectors } from 'state/secondaryMarket/cpOrder/cancellation/cancellations.selectors';
import { limitOrderCancellationSelectors } from 'state/secondaryMarket/limit-order/cancellation/cancellations.selectors';
import { liveCancellationSelectors } from 'state/secondaryMarket/live/cancellation/cancellations.selectors';
import { getLiveInstantBook } from 'state/secondaryMarket/live/instantBook/instantBook.selectors';
import {
  secondaryMarketLiveInstantBookRequestedThunk,
  secondaryMarketLiveInstantBookWorkflowAbortedThunk,
  secondaryMarketLiveInstantBookWorkflowCompletedThunk,
  secondaryMarketLiveInstantBookWorkflowInitThunk,
} from 'state/secondaryMarket/live/instantBook/instantBook.thunks';
import {
  secondaryMarketOrderCancellationRequestedThunk,
  secondaryMarketOrderCancellationWorkflowAbortedThunk,
  secondaryMarketOrderCancellationWorkflowCompletedThunk,
  secondaryMarketOrderCancellationWorkflowInitThunk,
  secondaryMarketOrderOpenNewOrderTabThunk,
  secondaryMarketOrderPostTradeEditThunk,
} from 'state/secondaryMarket/secondaryMarket.thunks';
import type { CancellationStatus } from 'state/secondaryMarket/shared/cancellation/cancellation.model';
import type { CancellationSelectors } from 'state/secondaryMarket/shared/cancellation/cancellationSelectorsFactory';
import { getUserFrontRights } from 'state/selectors';
import * as routerSelectors from 'state/selectors/common/router';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import {
  isSecondaryMarketAmendLiveTradeEnabledForUser,
  isSecondaryMarketCpOrderEnabledForUser,
  isSecondaryOrderBookEnabledForUser,
} from 'state/user';
import type { WorkflowStatus } from 'utils/status';

export type SelectedItem = {
  orderId: string;
  accruedInterest?: number;
  dealId?: string;
  canOpenOrder?: boolean;
  isin?: string;
  fileCode?: string;
  requester?: string;
  salesCounterpartName?: string;
  orderStatus?: string;
  price?: number;
  priceType?: string;
  currency?: string;
  nominal?: number;
  adjustedSalesMargin?: number;
  settlementQuantity?: number;
  settlementCashAmount?: number;
  tradeDate?: Date;
  valuationDate?: Date;
  cobDate?: Date;
  settlementDate?: Date;
  orderType?: OrderType;
  tradingPrice?: number;
  adjustedTradingPrice?: number;
  adjustedUpfrontFee?: number;
  salesMargin?: number;
  upfront?: number;
  clientLimit?: number;
};

interface StateProps {
  selected?: SelectedItem;
  isSecondaryMarketPostTradeRedirectionAllowed: boolean;
  isSecondaryMarketOrderCancellationAllowed: boolean;
  orderInstantBookStatus: WorkflowStatus | undefined;
  orderInstantBookError: string | undefined;
  orderCancellationStatus: CancellationStatus | undefined;
  orderCancellationError: string | undefined;
  isSecondaryOrderBookEnabled: boolean;
}

interface DispatchProps {
  onReset: () => void;
  onOpenSecondaryMarketPostTrade: (orderId: string, tabName: string) => void;
  onOpenNewOrderTab: (orderId: string) => void;
  onCancellationWorkflowInitiated: (orderId: string) => void;
  onCancellationWorkflowConfirmed: (orderId: string) => void;
  onCancellationWorkflowAborted: (orderId: string) => void;
  onCancellationWorkflowCompleted: (orderId: string) => void;
  onInstantBookWorkflowInitiated: (orderId: string) => void;
  onInstantBookWorkflowConfirmed: (orderId: string) => void;
  onInstantBookWorkflowAborted: (orderId: string) => void;
  onInstantBookWorkflowCompleted: (orderId: string) => void;
}

export type ComponentProps = StateProps & DispatchProps;

const orderCancellationSelectorsMap: Record<OrderType, CancellationSelectors> = {
  'Limit order': limitOrderCancellationSelectors,
  Live: liveCancellationSelectors,
  'Order on close': cpOrderCancellationSelectors,
};

const mapStateToProps = (state: AppState): StateProps => {
  const orderId = getSecondaryOrderBlotterPanelSelected(state);
  const item = getOrderById(state, orderId);
  if (orderId === undefined || item === undefined) {
    return {
      selected: undefined,
      isSecondaryMarketPostTradeRedirectionAllowed: false,
      isSecondaryMarketOrderCancellationAllowed: false,
      orderCancellationStatus: undefined,
      orderCancellationError: undefined,
      orderInstantBookStatus: undefined,
      orderInstantBookError: undefined,
      isSecondaryOrderBookEnabled: false,
    };
  }

  const isSecondaryMarketPostTradeRedirectionAllowed =
    item.orderType === 'Order on close'
      ? routerSelectors.isSecondaryPostEventVisible(state)
      : routerSelectors.isSecondaryPostEventVisible(state) && isSecondaryMarketAmendLiveTradeEnabledForUser(state);

  const isSecondaryMarketOrderCancellationAllowed = getUserFrontRights(state).secondaryOrderCancellationEnabled;
  const isSecondaryOrderBookEnabled = isSecondaryOrderBookEnabledForUser(state);

  const orderInstantBook = getLiveInstantBook(state, orderId);
  const orderCancellation = orderCancellationSelectorsMap[item.orderType].getOrderCancellation(state, orderId);

  const canOpenOrder = isSecondaryMarketCpOrderEnabledForUser(state);

  return {
    selected: {
      ...item,
      orderId,
      canOpenOrder,
    },
    isSecondaryMarketPostTradeRedirectionAllowed: isSecondaryMarketPostTradeRedirectionAllowed,
    isSecondaryMarketOrderCancellationAllowed: isSecondaryMarketOrderCancellationAllowed,
    orderCancellationStatus: orderCancellation?.status,
    orderCancellationError: orderCancellation?.error,
    orderInstantBookStatus: orderInstantBook?.status,
    orderInstantBookError: orderInstantBook?.error,
    isSecondaryOrderBookEnabled,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => {
  return {
    onReset: () => {
      dispatch(blotterPanelItemReset());
    },
    onOpenSecondaryMarketPostTrade: (orderId, tabName) => {
      dispatch(secondaryMarketOrderPostTradeEditThunk(orderId, tabName));
    },
    onOpenNewOrderTab: orderId => {
      dispatch(secondaryMarketOrderOpenNewOrderTabThunk(orderId));
    },
    onCancellationWorkflowAborted: orderId => {
      dispatch(secondaryMarketOrderCancellationWorkflowAbortedThunk(orderId));
    },
    onCancellationWorkflowCompleted: orderId => {
      dispatch(secondaryMarketOrderCancellationWorkflowCompletedThunk(orderId));
    },
    onCancellationWorkflowConfirmed: orderId => {
      dispatch(secondaryMarketOrderCancellationRequestedThunk(orderId));
    },
    onCancellationWorkflowInitiated: orderId => {
      dispatch(secondaryMarketOrderCancellationWorkflowInitThunk(orderId));
    },
    onInstantBookWorkflowAborted: orderId => {
      dispatch(secondaryMarketLiveInstantBookWorkflowAbortedThunk(orderId));
    },
    onInstantBookWorkflowCompleted: orderId => {
      dispatch(secondaryMarketLiveInstantBookWorkflowCompletedThunk(orderId));
    },
    onInstantBookWorkflowConfirmed: orderId => {
      dispatch(secondaryMarketLiveInstantBookRequestedThunk(orderId));
    },
    onInstantBookWorkflowInitiated: orderId => {
      dispatch(secondaryMarketLiveInstantBookWorkflowInitThunk(orderId));
    },
  };
};

export const connectSecondaryMarketOrderBlotter = connect(mapStateToProps, mapDispatchToProps);
